/**
 * 128 — IMEI and serial numbers, and the warranty a sold unit carries.
 *
 * A product the owner marks „IMEI / სერიული ნომერი" is sold unit by unit: the
 * till asks for every unit's number and the sale line keeps them
 * (`sale_items.serials`, one per piece). Any product may carry a warranty in
 * months; the SERVER stamps its end date on the line at the moment of sale
 * (private.warranty_until), and `warrantyUntil` below is the same rule for the
 * receipt printed before the server's copy is back.
 *
 * The normal form is the server's (private.clean_serials): no spaces at all,
 * upper case, at most 40 characters. The two must agree, or a number typed at
 * the counter would not find the sale it was sold on.
 */

export const SERIAL_MAX = 40

/**
 * A wedge scanner types through the computer's keyboard layout: with Georgian
 * switched on, a serial's letters arrive as Georgian ones (C02X… → ც02ხ…).
 * Each is turned back into the Latin key it sits on (the standard QWERTY-based
 * Georgian layout, shifted letters included). Digits are not affected.
 */
const KA_TO_LATIN: Record<string, string> = {
  'ქ': 'Q', 'წ': 'W', 'ჭ': 'W', 'ე': 'E', 'რ': 'R', 'ღ': 'R', 'ტ': 'T', 'თ': 'T',
  'ყ': 'Y', 'უ': 'U', 'ი': 'I', 'ო': 'O', 'პ': 'P', 'ა': 'A', 'ს': 'S', 'შ': 'S',
  'დ': 'D', 'ფ': 'F', 'გ': 'G', 'ჰ': 'H', 'ჯ': 'J', 'ჟ': 'J', 'კ': 'K', 'ლ': 'L',
  'ზ': 'Z', 'ძ': 'Z', 'ხ': 'X', 'ც': 'C', 'ჩ': 'C', 'ვ': 'V', 'ბ': 'B', 'ნ': 'N', 'მ': 'M',
}

/** Georgian letters back to the Latin keys they sit on; the rest untouched. */
export const latinKeys = (s: string) => s.replace(/[ა-ჿ]/g, (ch) => KA_TO_LATIN[ch] ?? ch)

export function normalizeSerial(raw: string): string {
  return latinKeys(raw.replace(/\s+/g, '')).toUpperCase()
}

/** A phone's IMEI: 15 digits, the last a Luhn check digit. */
export const isImeiShaped = (s: string) => /^\d{15}$/.test(s)

export function luhnValid(digits: string): boolean {
  if (!/^\d+$/.test(digits)) return false
  let sum = 0
  for (let i = 0; i < digits.length; i++) {
    let d = digits.charCodeAt(digits.length - 1 - i) - 48
    if (i % 2 === 1) {
      d *= 2
      if (d > 9) d -= 9
    }
    sum += d
  }
  return sum % 10 === 0
}

export type SerialProblem = 'empty' | 'tooShort' | 'tooLong' | 'chars' | 'checksum'

/**
 * Why a NORMALIZED number cannot be taken as it is — null when it can.
 * `checksum` is a warning the cashier may override: one mistyped digit is the
 * commonest error, but a real serial that merely looks like an IMEI exists.
 */
export function serialProblem(s: string): SerialProblem | null {
  if (!s) return 'empty'
  if (s.length < 4) return 'tooShort'
  if (s.length > SERIAL_MAX) return 'tooLong'
  if (!/^[0-9A-Z\-/.]+$/.test(s)) return 'chars'
  if (isImeiShaped(s) && !luhnValid(s)) return 'checksum'
  return null
}

/**
 * A code the till could not find as a product that may be a unit's number —
 * worth asking the server whether it was sold here. Eight characters at least,
 * so a mistyped short code does not open the warranty card, and a digit in it:
 * every IMEI and serial has one, while a product name typed on a Georgian
 * layout (კოკა-კოლა → KOKA-KOLA after normalizing) has none.
 */
export function looksLikeSerial(code: string): boolean {
  const s = normalizeSerial(code)
  return s.length >= 8 && s.length <= SERIAL_MAX && /^[0-9A-Z\-/.]+$/.test(s) && /\d/.test(s)
}

/** Georgia is UTC+4 all year (no daylight saving since 2005). */
const TBILISI_MS = 4 * 3600_000

/** Today's date in Tbilisi, YYYY-MM-DD. */
export function tbilisiToday(now: Date = new Date()): string {
  return new Date(now.getTime() + TBILISI_MS).toISOString().slice(0, 10)
}

/**
 * The day a warranty of `months` ends, counted from `from`'s Tbilisi date —
 * Postgres' `date + interval 'N months'`, which clamps to the month's last day
 * (31 January + 1 month = 28/29 February).
 */
export function warrantyUntil(months: number | null | undefined, from: Date = new Date()): string | null {
  if (!months || months <= 0) return null
  const t = new Date(from.getTime() + TBILISI_MS)
  const total = t.getUTCMonth() + months
  const y = t.getUTCFullYear() + Math.floor(total / 12)
  const m = total % 12
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate()
  const d = Math.min(t.getUTCDate(), last)
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

/** 2027-10-01 → 01.10.2027, the form the receipt and the counter read. */
export function dmy(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}.${m}.${y}`
}

export type WarrantyState =
  | { kind: 'none' }
  | { kind: 'active'; daysLeft: number }
  | { kind: 'expired'; daysAgo: number }

/** The warranty seen from `today` (YYYY-MM-DD): the end day itself still counts. */
export function warrantyState(until: string | null | undefined, today: string = tbilisiToday()): WarrantyState {
  if (!until) return { kind: 'none' }
  const days = Math.round((Date.parse(until.slice(0, 10)) - Date.parse(today)) / 86_400_000)
  return days >= 0 ? { kind: 'active', daysLeft: days } : { kind: 'expired', daysAgo: -days }
}

/** One sale or return that named a unit — a row of `serial_lookup`. */
export type SerialEvent = {
  sale_id: string
  type: 'sale' | 'return'
  created_at: string
  reversal_of: string | null
  branch_name: string | null
  cashier_name: string | null
  customer_name: string | null
  product_id: string | null
  product_name: string
  unit_price: number
  quantity: number
  total_price: number
  warranty_until: string | null
}

export type SerialLookup = { serial: string; events: SerialEvent[] }

/**
 * Where the unit stands: the newest event decides (the server orders them,
 * newest first). `sold` — out with a customer; `returned` — back on the shelf;
 * `unknown` — this shop never named it.
 */
export function serialState(events: SerialEvent[]): 'sold' | 'returned' | 'unknown' {
  if (events.length === 0) return 'unknown'
  return events[0].type === 'return' ? 'returned' : 'sold'
}

/** The sale that put the unit in its current owner's hands — for the warranty. */
export const lastSaleOf = (events: SerialEvent[]): SerialEvent | null =>
  events.find((e) => e.type === 'sale') ?? null
